/**
 * Manages the live price display element in the configurator.
 *
 * Shows a spinner while pricing is loading and updates with the displayed price
 * once the Lumaprints pricing API responds. For multi-panel products, the main
 * price is the total (per-panel Lumaprints price × production image count).
 */
export class PriceDisplay {

	private container: HTMLElement;
	private priceEl: HTMLElement;
	private spinnerEl: HTMLElement;
	private detailEl: HTMLElement;

	constructor(container: HTMLElement) {
		this.container = container;

		this.priceEl   = container.querySelector<HTMLElement>('.luma-configurator__price-value')
			?? this.createEl('span', 'luma-configurator__price-value');

		this.spinnerEl = container.querySelector<HTMLElement>('.luma-configurator__price-spinner')
			?? this.createEl('span', 'luma-configurator__price-spinner', { 'aria-hidden': 'true' });

		this.detailEl = container.querySelector<HTMLElement>('.luma-configurator__price-detail')
			?? this.createEl('span', 'luma-configurator__price-detail');

		this.spinnerEl.hidden = true;
		this.hide();
	}

	showHint(message: string): void {
		this.spinnerEl.hidden    = true;
		this.priceEl.textContent = message;
		this.clearDetail();
		this.container.hidden    = false;
		this.container.classList.add('luma-configurator__price--hint');
		this.container.classList.remove('luma-configurator__price--error');
		this.container.removeAttribute('aria-busy');
	}

	showLoading(): void {
		this.priceEl.textContent  = '';
		this.spinnerEl.hidden     = false;
		this.clearDetail();
		this.container.hidden     = false;
		this.container.classList.remove('luma-configurator__price--hint');
		this.container.classList.remove('luma-configurator__price--error');
		this.container.setAttribute('aria-busy', 'true');
	}

	/**
	 * @param displayedPricePerPanel — Lumaprints price for one panel after your multiplier (same value stored per cart line).
	 * @param panelCount — production images / panels (≥ 1). Main display = per-panel × count when count > 1.
	 */
	showPrice(displayedPricePerPanel: number, panelCount: number = 1): void {
		const panels = Math.max(1, panelCount);
		const total  = displayedPricePerPanel * panels;

		this.spinnerEl.hidden    = true;
		this.priceEl.textContent = this.formatCurrency(total);
		this.container.hidden    = false;
		this.container.classList.remove('luma-configurator__price--hint');
		this.container.classList.remove('luma-configurator__price--error');
		this.container.removeAttribute('aria-busy');

		if (panels > 1) {
			this.detailEl.textContent =
				`${panels} panels × ${this.formatCurrency(displayedPricePerPanel)} each (total for your artwork).`;
			this.detailEl.hidden = false;
		} else {
			this.clearDetail();
		}
	}

	showError(message: string): void {
		this.spinnerEl.hidden    = true;
		this.priceEl.textContent = message;
		this.clearDetail();
		this.container.hidden    = false;
		this.container.classList.remove('luma-configurator__price--hint');
		this.container.classList.add('luma-configurator__price--error');
		this.container.removeAttribute('aria-busy');
	}

	hide(): void {
		this.container.hidden = true;
		this.container.classList.remove('luma-configurator__price--hint');
		this.container.classList.remove('luma-configurator__price--error');
	}

	private clearDetail(): void {
		this.detailEl.textContent = '';
		this.detailEl.hidden      = true;
	}

	private formatCurrency(amount: number): string {
		return new Intl.NumberFormat('en-US', {
			style:    'currency',
			currency: 'USD',
		}).format(amount);
	}

	private createEl(tag: string, className: string, attrs: Record<string, string> = {}): HTMLElement {
		const el = document.createElement(tag);
		el.className = className;

		for (const [key, value] of Object.entries(attrs)) {
			el.setAttribute(key, value);
		}

		this.container.appendChild(el);

		return el;
	}
}
